import React, { useState, useEffect, useMemo } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import showToast from '../../components/Toast';
import { 
  FiMail, 
  FiSave, 
  FiTrash2, 
  FiEdit, 
  FiPlus, 
  FiCheckCircle,
  FiXCircle,
  FiServer,
  FiLock,
  FiUser,
  FiSend,
  FiRefreshCw,
  FiEye
} from 'react-icons/fi';
import AxiosInstance from '../../AxiosInstance';
import config from '../../config/frontend.config';
import { usePermissions } from '../../contexts/PermissionContext';
import PermissionGate from '../../components/PermissionGate';
import EnhancedUniversalTable from '../../components/EnhancedUniversalTable';
import CustomDropdown from '../../components/CustomDropdown';
import './SMTP.css';

// Create validation schema function to handle edit vs create
const createValidationSchema = (isEdit = false, isSuperAdmin = false) => Yup.object({
  name: Yup.string().required('SMTP name is required'),
  host: Yup.string().required('SMTP host is required'),
  port: Yup.number()
    .required('Port is required')
    .min(1, 'Port must be between 1 and 65535')
    .max(65535, 'Port must be between 1 and 65535'),
  secure: Yup.boolean(),
  authUser: Yup.string().required('Email/Username is required'),
  authPass: isEdit 
    ? Yup.string() // Optional for edits
    : Yup.string().required('Password is required'), // Required for new
  fromName: Yup.string(),
  fromEmail: Yup.string()
    .email('Invalid email address')
    .required('From email is required'),
  companyId: isSuperAdmin && !isEdit
    ? Yup.string().required('Please select a company')
    : Yup.string(),
  testEmail: Yup.string().email('Invalid test email address')
});

const SMTP = () => {
  const BASE_URL = config.api.baseURL;
  const API_PATH = config.api.apiPath;
  const API_ENDPOINT = `${BASE_URL}/${API_PATH}/smtp`;

  const { hasPermission } = usePermissions();
  const [smtpConfigs, setSmtpConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [testingConfig, setTestingConfig] = useState(null);
  const [testEmail, setTestEmail] = useState('');
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);

  // Check permissions
  const canView = hasPermission('canViewSMTP');
  const canCreate = hasPermission('canCreateSMTP');
  const canUpdate = hasPermission('canUpdateSMTP');
  const canDelete = hasPermission('canDeleteSMTP');
  const canTest = hasPermission('canTestSMTP');

  // Fetch SMTP configurations
  const fetchSMTPConfigs = async (showLoading = true) => {
    try {
      if (showLoading) setLoading(true);
      const response = await AxiosInstance.get(API_ENDPOINT);
      
      if (response?.data?.statusCode === 200) {
        setSmtpConfigs(response.data.data || []);
      } else {
        setSmtpConfigs([]);
      }
    } catch (error) {
      console.error('Error fetching SMTP configurations:', error);
      if (showLoading) {
        showToast.error('Failed to fetch SMTP configurations');
      }
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  // Check if user is superadmin
  useEffect(() => {
    const userData = config.getUserData();
    const userRole = config.getUserRole();
    setIsSuperAdmin(userRole === 'SuperAdmin' || userData?.Role === 'SuperAdmin' || userData?.isSuperAdmin);
  }, []);

  // Fetch companies with admin users when modal opens (for superadmin)
  const fetchCompanies = async () => {
    if (!isSuperAdmin) return;
    
    try {
      setLoadingCompanies(true);
      const response = await AxiosInstance.get(`${BASE_URL}/${API_PATH}/user/companies-with-admins`);
      
      if (response?.data?.statusCode === 200) {
        setCompanies(response.data.data || []);
      } else {
        setCompanies([]);
      }
    } catch (error) {
      console.error('Error fetching companies:', error);
      setCompanies([]);
    } finally {
      setLoadingCompanies(false);
    }
  };

  useEffect(() => {
    if (canView) {
      fetchSMTPConfigs();
    }
  }, [canView]);

  // Handle toggle isActive
  const handleToggleActive = async (smtpId, currentValue, event) => {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    
    if (!canUpdate) {
      showToast.error('You do not have permission to update SMTP configurations');
      return;
    }

    const newValue = !currentValue;
    
    // Save previous state for revert
    const previousState = smtpConfigs.map(c => ({ ...c }));
    
    // Optimistic update - update UI immediately for fast response
    if (newValue) {
      // If activating, all others should be inactive
      setSmtpConfigs(prevConfigs => 
        prevConfigs.map(config => 
          config.smtpId === smtpId 
            ? { ...config, isActive: true }
            : { ...config, isActive: false }
        )
      );
    } else {
      // If deactivating, ONLY update this one, don't touch others
      setSmtpConfigs(prevConfigs => 
        prevConfigs.map(config => 
          config.smtpId === smtpId 
            ? { ...config, isActive: false }
            : config // Keep others as they are
        )
      );
    }

    try {
      // Make API call in background
      const response = await AxiosInstance.put(
        `${API_ENDPOINT}/${smtpId}`,
        { isActive: newValue }
      );

      if (response?.data?.statusCode === 200) {
        // Update state based on server response
        if (newValue) {
          // If activating, all others should be inactive (confirmed by server)
          setSmtpConfigs(prevConfigs => 
            prevConfigs.map(config => 
              config.smtpId === smtpId 
                ? { ...config, isActive: true }
                : { ...config, isActive: false }
            )
          );
        } else {
          // If deactivating, just update this one (others remain unchanged)
          setSmtpConfigs(prevConfigs => 
            prevConfigs.map(config => 
              config.smtpId === smtpId 
                ? { ...config, isActive: false }
                : config
            )
          );
        }
        showToast.success(newValue ? 'SMTP configuration activated' : 'SMTP configuration deactivated');
      } else {
        // Revert on error - restore previous state
        setSmtpConfigs(previousState);
        showToast.error(response.data?.message || 'Failed to update active status');
      }
    } catch (error) {
      console.error('Error toggling active status:', error);
      // Revert on error - restore previous state
      setSmtpConfigs(previousState);
      const errorMessage = error.response?.data?.message || 'Failed to update active status';
      showToast.error(errorMessage);
    }
  };

  // Define table columns
  const columns = useMemo(() => [
    {
      key: 'name',
      title: 'Name',
      sortable: true,
      render: (value, row) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <FiServer style={{ color: '#667eea', fontSize: '18px' }} />
          <div>
            <div style={{ fontWeight: '600', color: '#111827', fontSize: '0.95rem' }}>
              {value}
            </div>
          </div>
        </div>
      )
    },
    {
      key: 'host',
      title: 'Host',
      sortable: true,
      render: (value) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <FiServer style={{ color: '#6b7280', fontSize: '16px' }} />
          <span style={{ fontWeight: '500', color: '#374151' }}>{value}</span>
        </div>
      )
    },
    {
      key: 'port',
      title: 'Port',
      sortable: true,
      render: (value, row) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <FiLock style={{ color: '#6b7280', fontSize: '16px' }} />
          <span style={{ fontWeight: '500', color: '#374151' }}>
            {value} ({row.secure ? 'SSL' : 'TLS'})
          </span>
        </div>
      )
    },
    {
      key: 'user',
      title: 'User',
      sortable: true,
      render: (value) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <FiUser style={{ color: '#6b7280', fontSize: '16px' }} />
          <span style={{ fontWeight: '500', color: '#374151' }}>{value || 'N/A'}</span>
        </div>
      )
    },
    {
      key: 'fromEmail',
      title: 'From Email',
      sortable: true,
      render: (value, row) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <FiMail style={{ color: '#6b7280', fontSize: '16px' }} />
          <div>
            {row.fromName ? (
              <div>
                <div style={{ fontWeight: '500', color: '#374151', fontSize: '0.9rem' }}>
                  {row.fromName}
                </div>
                <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>
                  {value}
                </div>
              </div>
            ) : (
              <span style={{ fontWeight: '500', color: '#374151' }}>{value}</span>
            )}
          </div>
        </div>
      )
    },
    {
      key: 'createdAt',
      title: 'Created',
      sortable: true,
      type: 'date',
      render: (value) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '0.9rem', fontWeight: '500', color: '#374151' }}>
            {new Date(value).toLocaleDateString()}
          </span>
        </div>
      )
    },
    {
      key: 'isActive',
      title: 'Status',
      sortable: true,
      render: (value, row) => {
        const isActive = row.isActive !== undefined && row.isActive !== null ? row.isActive : false;
        
        return (
          <div className="active-status-cell">
            <div 
              className="toggle-switch" 
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                handleToggleActive(row.id, isActive, e);
              }}
              style={{ cursor: canUpdate ? 'pointer' : 'not-allowed', opacity: canUpdate ? 1 : 0.6 }}
            >
              <div className={`toggle-slider ${isActive ? 'active' : 'inactive'}`}>
                <div className="toggle-knob"></div>
              </div>
            </div>
          </div>
        );
      }
    }
  ], [canUpdate]);

  // Transform data for table
  const tableData = useMemo(() => {
    return smtpConfigs.map(config => ({
      id: config.smtpId,
      name: config.name,
      host: config.host,
      port: config.port,
      secure: config.secure,
      user: config.user,
      fromName: config.fromName,
      fromEmail: config.fromEmail,
      isActive: config.isActive,
      createdAt: config.createdAt
    }));
  }, [smtpConfigs]);

  // Handle form submission
  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      // Use flat keys directly
      const apiData = {
        name: values.name,
        host: values.host,
        port: values.port,
        secure: values.secure,
        user: values.authUser,
        fromName: values.fromName || '',
        fromEmail: values.fromEmail,
      };

      // For superadmin, add CompanyId from form
      if (isSuperAdmin && values.companyId) {
        apiData.CompanyId = values.companyId;
      }

      let response;
      
      if (editingConfig) {
        // Update existing - only send password if it was changed
        if (values.authPass) {
          apiData.pass = values.authPass;
        }
        // Only send user if it was changed
        if (values.authUser) {
          apiData.user = values.authUser;
        }
        response = await AxiosInstance.put(
          `${API_ENDPOINT}/${editingConfig.smtpId}`,
          apiData
        );
      } else {
        // Create new - password is required
        apiData.pass = values.authPass;
        response = await AxiosInstance.post(API_ENDPOINT, apiData);
      }

      if (response?.data?.statusCode === 200 || response?.data?.statusCode === 201) {
        showToast.success(response.data.message || 'SMTP configuration saved successfully!');
        setShowModal(false);
        setEditingConfig(null);
        resetForm();
        fetchSMTPConfigs();
      } else {
        showToast.error(response.data?.message || 'Failed to save SMTP configuration');
      }
    } catch (error) {
      console.error('Error saving SMTP configuration:', error);
      showToast.error(error.response?.data?.message || 'Failed to save SMTP configuration');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle delete
  const handleDelete = async (smtpId) => {
    if (!window.confirm('Are you sure you want to delete this SMTP configuration?')) {
      return;
    }

    try {
      const response = await AxiosInstance.delete(`${API_ENDPOINT}/${smtpId}`);
      
      if (response?.data?.statusCode === 200) {
        showToast.success('SMTP configuration deleted successfully!');
        fetchSMTPConfigs();
      } else {
        showToast.error(response.data?.message || 'Failed to delete SMTP configuration');
      }
    } catch (error) {
      console.error('Error deleting SMTP configuration:', error);
      showToast.error(error.response?.data?.message || 'Failed to delete SMTP configuration');
    }
  };

  // Handle test email
  const handleTestEmail = async (smtpId, testEmailAddress) => {
    if (!testEmailAddress) {
      showToast.error('Please enter a test email address');
      return;
    }

    try {
      setTestingConfig(smtpId);
      const response = await AxiosInstance.post(
        `${API_ENDPOINT}/${smtpId}/test`,
        { testEmail: testEmailAddress }
      );

      if (response?.data?.statusCode === 200) {
        showToast.success('Test email sent successfully! Please check your inbox.');
      } else {
        showToast.error(response.data?.message || 'Failed to send test email');
      }
    } catch (error) {
      console.error('Error testing SMTP configuration:', error);
      showToast.error(error.response?.data?.message || 'Failed to send test email');
    } finally {
      setTestingConfig(null);
    }
  };

  // Open edit modal
  const handleEdit = (config) => {
    setEditingConfig(config);
    setShowModal(true);
  };

  // Open create modal
  const handleCreate = () => {
    setEditingConfig(null);
    setShowModal(true);
    if (isSuperAdmin) {
      fetchCompanies();
    }
  };

  // Close modal
  const handleCloseModal = () => {
    setShowModal(false);
    setEditingConfig(null);
    setTestEmail('');
  };

  const initialValues = editingConfig ? {
    name: editingConfig.name || '',
    host: editingConfig.host || '',
    port: editingConfig.port || 587,
    secure: editingConfig.secure || false,
    authUser: editingConfig.user || '',
    authPass: '', // Don't pre-fill password for security
    fromName: editingConfig.fromName || '',
    fromEmail: editingConfig.fromEmail || '',
    companyId: editingConfig.CompanyId || ''
  } : {
    name: '',
    host: '',
    port: 587,
    secure: false,
    authUser: '',
    authPass: '',
    fromName: '',
    fromEmail: '',
    companyId: ''
  };

  return (
    <PermissionGate 
      requiredPermissions={['canViewSMTP']}
      customMessage="You don't have permission to view SMTP configurations"
    >
      <div className="smtp-container">
        <div className="smtp-header">
          <div className="header-left">
            <h1>
              <FiMail className="header-icon" />
              SMTP Configuration
            </h1>
            <p>Configure SMTP settings for sending emails</p>
          </div>
          {canCreate && (
            <button 
              className="btn-primary"
              onClick={handleCreate}
            >
              <FiPlus /> Add SMTP
            </button>
          )}
        </div>

        {/* Table */}
        <div className="table-container">
          {loading ? (
            <div className="loading-state">Loading SMTP configurations...</div>
          ) : (
            <EnhancedUniversalTable
              data={tableData}
              columns={columns}
              loading={loading}
              onRowClick={(row) => {
                const config = smtpConfigs.find(c => c.smtpId === row.id);
                if (config) {
                  handleEdit(config);
                }
              }}
              actions={[
                ...(canTest ? [{
                  key: 'test',
                  icon: <FiSend />,
                  title: 'Test SMTP',
                  variant: 'info',
                  show: () => true
                }] : []),
                ...(canUpdate ? [{
                  key: 'edit',
                  icon: <FiEdit />,
                  title: 'Edit',
                  variant: 'primary',
                  show: () => true
                }] : []),
                ...(canDelete ? [{
                  key: 'delete',
                  icon: <FiTrash2 />,
                  title: 'Delete',
                  variant: 'danger',
                  show: () => true
                }] : [])
              ]}
              onActionClick={(actionKey, row) => {
                const config = smtpConfigs.find(c => c.smtpId === row.id);
                if (!config) return;

                if (actionKey === 'test') {
                  if (testingConfig === config.smtpId) {
                    showToast.warning('Test email is already being sent. Please wait...');
                    return;
                  }
                  const email = prompt('Enter test email address:');
                  if (email) {
                    handleTestEmail(config.smtpId, email);
                  }
                } else if (actionKey === 'edit') {
                  handleEdit(config);
                } else if (actionKey === 'delete') {
                  handleDelete(config.smtpId);
                }
              }}
              searchable={true}
              filterable={false}
              sortable={true}
              pagination={true}
              pageSize={10}
              emptyMessage="No SMTP configurations found. Click 'Add SMTP' to create your first configuration."
            />
          )}
        </div>

        {/* Create/Edit Modal */}
        {showModal && (
          <div className="modal-overlay" onClick={handleCloseModal}>
            <div className="modal-content smtp-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{editingConfig ? 'Edit SMTP Configuration' : 'Add SMTP Configuration'}</h2>
                <button className="close-btn" onClick={handleCloseModal}>×</button>
              </div>
              
              <Formik
                initialValues={initialValues}
                validationSchema={createValidationSchema(!!editingConfig, isSuperAdmin && !editingConfig)}
                onSubmit={handleSubmit}
                enableReinitialize
              >
                {({ isSubmitting, values, setFieldValue }) => (
                  <Form className="smtp-form">
                    {/* Company Selection for SuperAdmin */}
                    {isSuperAdmin && !editingConfig && (
                      <div className="form-group">
                        <label htmlFor="companyId">
                          <FiUser /> Select Company *
                        </label>

                        {loadingCompanies ? (
                          <div style={{ padding: '10px', textAlign: 'center', color: '#6b7280' }}>
                            Loading companies...
                          </div>
                        ) : (
                          <CustomDropdown
                            options={companies.map(company => ({
                              value: company.CompanyId,
                              label: `${company.CompanyName} (${company.AdminUsers?.length || 0} admin${company.AdminUsers?.length !== 1 ? 's' : ''})`,
                              data: company
                            }))}

                            value={
                              values.companyId
                                ? {
                                    value: values.companyId,
                                    label:
                                      companies.find(c => c.CompanyId === values.companyId)
                                        ?.CompanyName || 'Selected Company'
                                  }
                                : null
                            }

                            onChange={(option) => {
                              const companyId = option?.value || '';
                              if (companyId !== values.companyId) {
                                setFieldValue('companyId', companyId);
                              }
                            }}

                            placeholder="-- Select Company --"
                            className="company-dropdown"
                            searchable={true}
                            textColor="white"
                          />
                        )}

                        <ErrorMessage
                          name="companyId"
                          component="div"
                          className="error-message"
                        />
                      </div>
                    )}

                    <div className="form-group">
                      <label htmlFor="name">
                        <FiServer /> Configuration Name *
                      </label>
                      <Field 
                        type="text" 
                        id="name" 
                        name="name" 
                        placeholder="e.g., Gmail SMTP"
                      />
                      <ErrorMessage name="name" component="div" className="error-message" />
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="host">
                          <FiServer /> SMTP Host *
                        </label>
                        <Field 
                          type="text" 
                          id="host" 
                          name="host" 
                          placeholder="smtp.gmail.com"
                        />
                        <ErrorMessage name="host" component="div" className="error-message" />
                      </div>

                      <div className="form-group">
                        <label htmlFor="port">
                          <FiLock /> Port *
                        </label>
                        <Field 
                          type="number" 
                          id="port" 
                          name="port" 
                          placeholder="587"
                        />
                        <ErrorMessage name="port" component="div" className="error-message" />
                      </div>
                    </div>

                    {/* <div className="form-group">
                      <label>
                        <Field 
                          type="checkbox" 
                          name="secure"
                        />
                        Use SSL (port 465) instead of TLS
                      </label>
                    </div> */}

                    <div className="form-group">
                      <label htmlFor="authUser">
                        <FiUser /> Email/Username *
                      </label>
                      <Field 
                        type="text" 
                        id="authUser" 
                        name="authUser" 
                        placeholder="your-email@gmail.com"
                      />
                      <ErrorMessage name="authUser" component="div" className="error-message" />
                    </div>

                    <div className="form-group">
                      <label htmlFor="authPass">
                        <FiLock /> Password/App Password *
                      </label>
                      <Field 
                        type="password" 
                        id="authPass" 
                        name="authPass" 
                        placeholder={editingConfig ? "Leave blank to keep current password" : "Enter password"}
                      />
                      <ErrorMessage name="authPass" component="div" className="error-message" />
                      {editingConfig && (
                           <>
                           </>
                      )}
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="fromName">
                          <FiUser /> From Name
                        </label>
                        <Field 
                          type="text" 
                          id="fromName" 
                          name="fromName" 
                          placeholder="Your Name"
                        />
                        <ErrorMessage name="fromName" component="div" className="error-message" />
                      </div>

                      <div className="form-group">
                        <label htmlFor="fromEmail">
                          <FiMail /> From Email *
                        </label>
                        <Field 
                          type="email" 
                          id="fromEmail" 
                          name="fromEmail" 
                          placeholder="sender@example.com"
                        />
                        <ErrorMessage name="fromEmail" component="div" className="error-message" />
                      </div>
                    </div>


                    <div className="modal-footer">
                      <button 
                        type="button" 
                        className="btn-secondary" 
                        onClick={handleCloseModal}
                      >
                        Cancel
                      </button>
                      <button 
                        type="submit" 
                        className="btn-primary" 
                        disabled={isSubmitting}
                      >
                        <FiSave /> {isSubmitting ? 'Saving...' : 'Save Configuration'}
                      </button>
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          </div>
        )}
      </div>
    </PermissionGate>
  );
};

export default SMTP;

